/* =====================================================================
 * Tooltip — Portal 기반 툴팁 (overflow: hidden 잘림 방지)
 * ===================================================================== */

import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: var(--tip-base-transform) translateY(4px); }
  to   { opacity: 1; transform: var(--tip-base-transform) translateY(0); }
`;

const fadeInDown = keyframes`
  from { opacity: 0; transform: var(--tip-base-transform) translateY(-4px); }
  to   { opacity: 1; transform: var(--tip-base-transform) translateY(0); }
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const Tip = styled.div<{ $position: 'top' | 'bottom' }>`
  position: fixed;
  background: ${({ theme }) => theme.colors.bgTooltip};
  color: ${({ theme }) => theme.colors.textInverse};
  font-size: 0.75rem;
  padding: 6px 10px;
  border-radius: 6px;
  max-width: min(280px, calc(100vw - 24px));
  white-space: normal;
  line-height: 1.45;
  word-break: keep-all;
  overflow-wrap: break-word;
  pointer-events: none;
  z-index: 99999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  animation: ${({ $position }) => $position === 'bottom' ? fadeInDown : fadeInUp} 0.15s forwards;
`;

interface TooltipProps {
  label: string;
  children: React.ReactNode;
  /** 툴팁 위치: 'top' (기본) 또는 'bottom' */
  position?: 'top' | 'bottom';
  /** 툴팁 수평 정렬: 'center' (기본), 'start' (왼쪽 정렬), 'end' (오른쪽 정렬) */
  align?: 'center' | 'start' | 'end';
}

interface TipCoords {
  top: number;
  left: number;
  /* CSS 변수로 전달할 기본 transform (애니메이션의 --tip-base-transform 에 사용) */
  baseTransform: string;
  /* 최종 initial transform (기본 + 수직 이동 없는 상태) */
  transform: string;
}

function calcCoords(
  rect: DOMRect,
  position: 'top' | 'bottom',
  align: 'center' | 'start' | 'end',
  gap = 6,
): TipCoords {
  let top: number;
  let left: number;
  let alignTransform = '';

  if (position === 'bottom') {
    top = rect.bottom + gap;
  } else {
    top = rect.top - gap;
  }

  if (align === 'start') {
    left = rect.left;
  } else if (align === 'end') {
    left = rect.right;
    alignTransform = 'translateX(-100%)';
  } else {
    left = rect.left + rect.width / 2;
    alignTransform = 'translateX(-50%)';
  }

  const verticalTransform = position === 'top' ? 'translateY(-100%)' : '';
  const baseTransform = [verticalTransform, alignTransform].filter(Boolean).join(' ') || 'none';

  return { top, left, baseTransform, transform: baseTransform };
}

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  children,
  position = 'top',
  align = 'center',
}) => {
  const [coords, setCoords] = useState<TipCoords | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords(calcCoords(rect, position, align));
    }, 400);
  }, [position, align]);

  const handleLeave = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setCoords(null);
  }, []);

  const portal =
    coords
      ? ReactDOM.createPortal(
          <Tip
            $position={position}
            style={{
              top: coords.top,
              left: coords.left,
              transform: coords.transform,
              ['--tip-base-transform' as string]: coords.baseTransform,
            }}
          >
            {label}
          </Tip>,
          document.body,
        )
      : null;

  return (
    <Wrapper ref={wrapperRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {children}
      {portal}
    </Wrapper>
  );
};

